import React, { useEffect, useState } from "react";
import { api, fmtMoney } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Wallet, TrendingUp } from "lucide-react";

const currentMonth = () => new Date().toISOString().slice(0, 7);

function MetricBox({ label, value, sub }) {
  return (
    <div className="bg-white border border-black/15 p-5">
      <div className="overline mb-2">{label}</div>
      <div className="font-mono font-bold text-2xl">{value}</div>
      {sub && <div className="font-mono text-xs text-[var(--text-secondary)] mt-2">{sub}</div>}
    </div>
  );
}

export default function Income() {
  const { user } = useAuth();
  const currency = user?.currency || "USD";
  const [month, setMonth] = useState(currentMonth());
  const [amount, setAmount] = useState("");
  const [source, setSource] = useState("Salary");
  const [history, setHistory] = useState([]);
  const [summary, setSummary] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const [hRes, sRes, curRes] = await Promise.all([
      api.get("/income"),
      api.get("/summary", { params: { month } }),
      api.get("/income", { params: { month } }),
    ]);
    setHistory(hRes.data);
    setSummary(sRes.data);
    setAmount(String(curRes.data.amount || ""));
    setSource(curRes.data.source || "Salary");
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [month]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/income", { month, amount: parseFloat(amount) || 0, source });
      toast.success("Income saved");
      load();
    } catch (err) {
      toast.error("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  // 50/30/20 allocation suggestions
  const income = parseFloat(amount) || 0;
  const needs = income * 0.5;
  const wants = income * 0.3;
  const savingsAlloc = income * 0.2;

  return (
    <div className="space-y-6" data-testid="income-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="overline">PAYCHECK</div>
          <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter">Salary &amp; Income</h1>
          <p className="font-mono text-sm text-[var(--text-secondary)] mt-2">
            Set monthly income to unlock savings rate, budget allocations, and smarter coaching.
          </p>
        </div>
        <input
          data-testid="income-month"
          type="month" value={month} onChange={(e) => setMonth(e.target.value)}
          className="bg-white border-2 border-black px-3 py-2 font-mono text-sm"
        />
      </div>

      <form onSubmit={save} className="bg-white border-2 border-black p-6 grid sm:grid-cols-3 gap-4 items-end" data-testid="income-form">
        <label className="block sm:col-span-2">
          <div className="overline mb-1">MONTHLY INCOME ({currency})</div>
          <input
            data-testid="income-amount"
            type="number" min="0" step="0.01" required
            value={amount} onChange={(e) => setAmount(e.target.value)}
            className="w-full bg-transparent border-b-2 border-black focus:border-[var(--action-blue)] outline-none py-2 text-4xl font-display font-black tracking-tighter"
          />
        </label>
        <label className="block">
          <div className="overline mb-1">SOURCE</div>
          <input
            data-testid="income-source"
            value={source} onChange={(e) => setSource(e.target.value)}
            className="w-full bg-transparent border-b-2 border-black/20 focus:border-black outline-none py-2 font-mono text-lg"
          />
        </label>
        <div className="sm:col-span-3">
          <button data-testid="save-income" disabled={saving}
            className="bg-black text-white px-8 py-3 font-display font-bold tracking-wider hover:bg-[var(--action-blue)] transition-colors disabled:opacity-50">
            {saving ? "SAVING…" : "SAVE INCOME →"}
          </button>
        </div>
      </form>

      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <MetricBox
            label="SAVINGS THIS MONTH"
            value={fmtMoney(summary.savings, currency)}
            sub={summary.income > 0 ? `${summary.savings_rate}% savings rate` : "Set income to compute"}
          />
          <MetricBox label="SPENT" value={fmtMoney(summary.total, currency)} sub={`of ${fmtMoney(summary.income, currency)} income`} />
          <MetricBox label="MONTH" value={summary.month} sub={`${summary.flags?.length || 0} red flag${summary.flags?.length === 1 ? "" : "s"}`} />
        </div>
      )}

      {/* Allocation suggestion */}
      {income > 0 && (
        <div className="bg-[#111111] text-white p-6">
          <div className="overline text-white/60 flex items-center gap-2"><TrendingUp size={12} /> 50 / 30 / 20 ALLOCATION</div>
          <h3 className="font-display font-bold text-xl mt-2 mb-4">Recommended split for this paycheck</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 font-mono">
            <div className="border border-white/30 p-4">
              <div className="overline text-white/60">NEEDS (50%)</div>
              <div className="text-2xl font-bold mt-1">{fmtMoney(needs, currency)}</div>
              <div className="text-xs text-white/60 mt-1">Bills, food, transport</div>
            </div>
            <div className="border border-white/30 p-4">
              <div className="overline text-white/60">WANTS (30%)</div>
              <div className="text-2xl font-bold mt-1">{fmtMoney(wants, currency)}</div>
              <div className="text-xs text-white/60 mt-1">Entertainment, shopping</div>
            </div>
            <div className="border-2 border-[var(--success-green)] p-4">
              <div className="overline" style={{ color: "var(--success-green)" }}>SAVE (20%)</div>
              <div className="text-2xl font-bold mt-1">{fmtMoney(savingsAlloc, currency)}</div>
              <div className="text-xs text-white/60 mt-1">Toward your goals</div>
            </div>
          </div>
        </div>
      )}

      {/* History */}
      <div className="bg-white border border-black/15">
        <div className="border-b-2 border-black p-4 flex items-center gap-2">
          <Wallet size={16} />
          <div className="overline">INCOME HISTORY</div>
        </div>
        {history.length === 0 ? (
          <div className="p-8 text-center font-mono text-sm text-[var(--text-secondary)]" data-testid="empty-income">No income recorded yet.</div>
        ) : (
          <table className="w-full font-mono text-sm">
            <thead>
              <tr className="border-b border-black/15">
                <th className="text-left p-3 overline">MONTH</th>
                <th className="text-left p-3 overline">SOURCE</th>
                <th className="text-right p-3 overline">AMOUNT</th>
              </tr>
            </thead>
            <tbody>
              {history.map((h) => (
                <tr key={h.month} className="border-b border-black/10">
                  <td className="p-3">{h.month}</td>
                  <td className="p-3 text-[var(--text-secondary)]">{h.source}</td>
                  <td className="p-3 text-right font-bold">{fmtMoney(h.amount, currency)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
